import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DarkModeContext } from "../../context/DarkModeContext";
import { getToken } from "../../utils/auth";
import { defaultFilters, fetchCampaignOptions, fetchDashboard } from "./dashboardApi";
import {
    ZMLayout,
    ZMCard,
    ZMMetricCard,
    ZMTable,
    ZMFiltersBar,
    ZMEmptyState,
    ZMLoadingState,
    ZMErrorState,
} from "./components/ZMUI";
import { Icono } from "./iconos";

const fmt = (n, dec = 0) =>
    n == null ? "0" : Number(n).toLocaleString("es-AR", { minimumFractionDigits: dec, maximumFractionDigits: dec });
const fmtUsd = (n) => `US$ ${fmt(n || 0, 2)}`;
const fmtPct = (n) => `${fmt(n || 0, 2)}%`;

export default function ZMUbicaciones() {
    const { darkMode } = useContext(DarkModeContext);
    const navigate = useNavigate();
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState("");
    const [filters, setFilters] = useState(() => defaultFilters());
    const [draftFilters, setDraftFilters] = useState(() => defaultFilters());
    const [placementFilter, setPlacementFilter] = useState("");
    const [campaignOptions, setCampaignOptions] = useState([]);
    const [ubicaciones, setUbicaciones] = useState({ filas: [], meta: {}, kpis: {}, campaignOptions: [] });
    const [creatividades, setCreatividades] = useState({ filas: [], meta: {}, campaignOptions: [] });

    const cargar = async (nextFilters = filters) => {
        const token = getToken();
        if (!token) {
            navigate("/");
            return;
        }

        setCargando(true);
        setError("");
        try {
            const [u, c, optionItems] = await Promise.all([
                fetchDashboard("ubicaciones", token, nextFilters),
                fetchDashboard("creatividades", token, nextFilters),
                fetchCampaignOptions(token, nextFilters),
            ]);
            setUbicaciones(u || { filas: [], meta: {}, kpis: {}, campaignOptions: [] });
            setCreatividades(c || { filas: [], meta: {}, campaignOptions: [] });
            setCampaignOptions(optionItems);
        } catch (e) {
            if (e?.code === 401 || e?.code === 403 || e?.message === "unauthorized") {
                navigate("/");
                return;
            }
            setError("No se pudieron cargar ubicaciones ni creatividades.");
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => {
        cargar(filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    const placements = useMemo(() => {
        const base = (ubicaciones.filas || []).map((item) => ({
            ...item,
            ubicacion: item.ubicacion || `${item.publisherPlatform || "Meta"} ${item.platformPosition || ""}`.trim(),
        }));
        return placementFilter ? base.filter((x) => x.ubicacion === placementFilter) : base;
    }, [ubicaciones.filas, placementFilter]);

    const placementOptions = useMemo(
        () => [...new Set((ubicaciones.filas || []).map((item) => item.ubicacion).filter(Boolean))].sort(),
        [ubicaciones.filas]
    );

    const creatives = useMemo(() => creatividades.filas || [], [creatividades.filas]);
    const kpis = ubicaciones.kpis || {};

    const totalLeadsMeta = placements.reduce((sum, item) => sum + (item.leadsMetaReported ?? item.leads ?? 0), 0);
    const inversion = placements.reduce((sum, item) => sum + (item.inversion || 0), 0);
    const alcance = placements.reduce((sum, item) => sum + (item.alcance || 0), 0);
    const cplPromedioMeta = totalLeadsMeta > 0 ? inversion / totalLeadsMeta : 0;

    if (cargando) return <ZMLoadingState label="Cargando ubicaciones..." fullScreen />;
    if (error) return <ZMErrorState message={error} onRetry={() => cargar(filters)} />;

    return (
        <ZMLayout
            title="Ubicaciones y creatividades"
            subtitle="Performance por placement y piezas creativas, separando leads CRM de leads reportados por Meta."
            right={ubicaciones?.meta?.mensaje || "Actualizado"}
            className={darkMode ? "container-dark" : ""}
        >
            <ZMFiltersBar className="mb-3">
                <div>
                    <label>Desde</label>
                    <input type="date" className="form-control form-control-sm" value={draftFilters.fechaDesde || ""} onChange={(e) => setDraftFilters((p) => ({ ...p, fechaDesde: e.target.value }))} />
                </div>
                <div>
                    <label>Hasta</label>
                    <input type="date" className="form-control form-control-sm" value={draftFilters.fechaHasta || ""} onChange={(e) => setDraftFilters((p) => ({ ...p, fechaHasta: e.target.value }))} />
                </div>
                <div style={{ minWidth: 260 }}>
                    <label>Campaña</label>
                    <select className="form-select form-select-sm" value={draftFilters.campania || ""} onChange={(e) => setDraftFilters((p) => ({ ...p, campania: e.target.value }))}>
                        <option value="">Todas las campañas</option>
                        {campaignOptions.map((item) => <option key={item.id} value={item.id}>{item.nombre}</option>)}
                    </select>
                </div>
                <div style={{ minWidth: 220 }}>
                    <label>Placement</label>
                    <select className="form-select form-select-sm" value={placementFilter} onChange={(e) => setPlacementFilter(e.target.value)}>
                        <option value="">Todos</option>
                        {placementOptions.map((item) => <option key={item} value={item}>{item}</option>)}
                    </select>
                </div>
                <div className="d-flex gap-2">
                    <button type="button" className="btn btn-sm zm-btn-primary" onClick={() => {
                        setFilters({ ...draftFilters });
                        cargar({ ...draftFilters });
                    }}>
                        Aplicar
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setPlacementFilter("")}>Limpiar placement</button>
                </div>
            </ZMFiltersBar>

            <div className="zm-grid-metrics">
                <ZMMetricCard icon={<Icono.Users />} label="Leads CRM" value={fmt(kpis.leadsCrm, 0)} helper="Fuente de verdad comercial" empty={!kpis.leadsCrm} />
                <ZMMetricCard icon={<Icono.BarChart />} label="Leads reportados por Meta" value={fmt(kpis.leadsMetaReported ?? totalLeadsMeta, 0)} helper="Actions/breakdowns de Meta Ads" empty={!kpis.leadsMetaReported && totalLeadsMeta === 0} />
                <ZMMetricCard icon={<Icono.AlertTriangle />} label="Diferencia" value={fmt(kpis.leadsDiferencia, 0)} helper="Meta reportados - Leads CRM" empty={kpis.leadsDiferencia == null} />
                <ZMMetricCard icon={<Icono.Dollar />} label="CPL Meta" value={fmtUsd(cplPromedioMeta)} />
                <ZMMetricCard icon={<Icono.Target />} label="Alcance" value={fmt(alcance, 0)} />
                <ZMMetricCard icon={<Icono.ImageIcon />} label="Creatividades" value={fmt(creatives.length, 0)} />
            </div>

            <ZMCard className="mb-3" bodyClassName="py-2">
                <div className="zm-inline-note">
                    <strong>Importante:</strong> en placements usamos datos de Meta para inversión, CTR y leads reportados. En creatividades priorizamos <b>leads CRM</b> como métrica principal para no inflar rendimiento con acciones de Meta no persistidas en LeadsMeta.
                </div>
            </ZMCard>

            {placements.length === 0 ? (
                <ZMEmptyState title="Sin ubicaciones disponibles" hint="Meta no devolvió placements para este rango o faltan permisos de breakdowns." />
            ) : (
                <>
                    <div className="row g-3 mb-3">
                        {placements.slice(0, 4).map((item, index) => (
                            <div key={`${item.ubicacion}-${index}`} className="col-12 col-sm-6 col-xl-3">
                                <ZMCard className="h-100">
                                    <div className="zm-placement-title">{item.ubicacion}</div>
                                    <div className="zm-placement-metrics">
                                        <span>Leads Meta <strong>{fmt(item.leadsMetaReported ?? item.leads, 0)}</strong></span>
                                        <span>CPL Meta <strong>{fmtUsd(item.cpl)}</strong></span>
                                        <span>CTR <strong>{fmtPct(item.ctr)}</strong></span>
                                        <span>Alcance <strong>{fmt(item.alcance, 0)}</strong></span>
                                    </div>
                                </ZMCard>
                            </div>
                        ))}
                    </div>

                    <div className="row g-3 mb-3">
                        <div className="col-12 col-lg-5">
                            <ZMCard title="Ranking por CPL Meta">
                                <div className="zm-stat-list">
                                    {[...placements].sort((a, b) => (a.cpl ?? 999999) - (b.cpl ?? 999999)).map((item, index) => (
                                        <div key={`${item.ubicacion}-${index}`} className="zm-stat-item">
                                            <div>
                                                <strong>{index + 1}. {item.ubicacion}</strong>
                                                <span>{fmt(item.leadsMetaReported ?? item.leads, 0)} leads Meta</span>
                                            </div>
                                            <b>{fmtUsd(item.cpl)}</b>
                                        </div>
                                    ))}
                                </div>
                            </ZMCard>
                        </div>
                        <div className="col-12 col-lg-7">
                            <ZMCard title="Matriz de rendimiento por placement">
                                <ZMTable
                                    emptyText="Sin filas de ubicaciones."
                                    columns={[
                                        { key: "ubicacion", header: "Ubicación" },
                                        { key: "leadsMetaReported", header: "Leads Meta", cellClassName: "text-end", render: (item) => fmt(item.leadsMetaReported ?? item.leads, 0) },
                                        { key: "cpl", header: "CPL Meta", cellClassName: "text-end", render: (item) => fmtUsd(item.cpl) },
                                        { key: "ctr", header: "CTR", cellClassName: "text-end", render: (item) => fmtPct(item.ctr) },
                                        { key: "alcance", header: "Alcance", cellClassName: "text-end", render: (item) => fmt(item.alcance, 0) },
                                    ]}
                                    rows={placements}
                                />
                            </ZMCard>
                        </div>
                    </div>

                    <ZMCard title="Rendimiento por creatividades" subtitle="Ads y piezas con leads CRM como métrica principal">
                        {creatives.length === 0 ? (
                            <ZMEmptyState title="Sin creatividades" hint="Meta no devolvió anuncios/creatives para el rango actual." />
                        ) : (
                            <div className="row g-3">
                                {creatives.slice(0, 8).map((ad) => (
                                    <div key={ad.adId} className="col-12 col-sm-6 col-xl-3">
                                        <div className="zm-creative-card">
                                            {ad.thumbnailUrl ? (
                                                <img src={ad.thumbnailUrl} alt={ad.nombreAnuncio} className="zm-creative-thumb" />
                                            ) : (
                                                <div className="zm-creative-thumb placeholder"><Icono.ImageIcon width={28} height={28} color="#9aa7b9" /></div>
                                            )}
                                            <div className="zm-creative-body">
                                                <strong>{ad.nombreAnuncio || "Creative"}</strong>
                                                <span>{ad.campaignName || ad.campaignId || "Sin campaña"}</span>
                                                <div className="zm-creative-stats">
                                                    <span>Leads CRM {fmt(ad.leadsCrm ?? ad.leads, 0)}</span>
                                                    <span>Leads Meta {fmt(ad.leadsMetaReported, 0)}</span>
                                                    <span>CPL CRM {fmtUsd(ad.cpl)}</span>
                                                    <span>CPL Meta {fmtUsd(ad.cplMetaReported)}</span>
                                                    <span>CTR {fmtPct(ad.ctr)}</span>
                                                    <span>Alcance {fmt(ad.alcance, 0)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </ZMCard>
                </>
            )}
        </ZMLayout>
    );
}
